/**
 * Predictive Operations Module Configuration
 *
 * Risk forecasting, scenario planning, and proactive maintenance scheduling.
 * Adapts to industry-specific risk terminology and factors.
 */

import { ModuleConfig } from './moduleTypes'

export const predictiveOpsConfig: ModuleConfig = {
  id: 'predictive-ops',
  name: 'Predictive Operations',
  description: 'Risk forecasting and proactive maintenance planning',
  icon: 'TrendingUp',
  basePath: '/predictions',
  enabledIndustries: ['manufacturing', 'oil_gas', 'healthcare', 'financial', 'energy', 'transportation', 'mining', 'chemicals', 'food_beverage', 'pharmaceuticals', 'aerospace', 'metals', 'iot_telecom', 'utilities', 'pulp_paper', 'automotive', 'datacenter', 'facility_management',
    // Phase 4 — non-industrial + medical-grade verticals (2026-06-27)
    'aquaculture_lobster', 'aquaculture_salmon', 'aquaculture_shrimp',
    'biosensor_ide_wearable', 'biosensor_continuous_glucose', 'biosensor_oximeter_fleet',
    'wearable_etd'],

  subPages: [
    {
      id: 'overview',
      path: '',
      name: 'Risk Overview',
      icon: 'Gauge',
      description: 'Fleet-wide risk distribution and key metrics',
      component: 'RiskOverview',
      requiredPermissions: ['predictions:read'],
      position: 1,
      industryLabels: {
        healthcare: 'Device Risk Overview',
        financial: 'Portfolio Risk Overview',
        transportation: 'Fleet Risk Overview',
        iot_telecom: 'Connectivity Risk Overview',
      },
    },
    {
      id: 'forecast',
      path: 'forecast',
      name: 'Anomaly Forecast',
      icon: 'LineChart',
      description: 'Predicted anomaly trends with confidence intervals',
      component: 'AnomalyForecast',
      requiredPermissions: ['predictions:read'],
      position: 2,
      industryLabels: {
        healthcare: 'Device Forecast',
        financial: 'Risk Forecast',
        iot_telecom: 'Connectivity Forecast',
      },
    },
    {
      id: 'risk-matrix',
      path: 'risk-matrix',
      name: 'Risk Matrix',
      icon: 'Grid3X3',
      description: 'Probability vs impact visualization',
      component: 'RiskMatrixView',
      requiredPermissions: ['predictions:read'],
      position: 3,
    },
    {
      id: 'scenarios',
      path: 'scenarios',
      name: 'What-If Simulator',
      icon: 'FlaskConical',
      description: 'Simulate scenarios and analyze outcomes',
      component: 'WhatIfSimulator',
      requiredPermissions: ['analytics:read'],
      position: 4,
      industryLabels: {
        financial: 'Stress Testing',
      },
    },
    {
      id: 'schedule',
      path: 'schedule',
      name: 'Maintenance Windows',
      icon: 'Calendar',
      description: 'Optimal maintenance scheduling based on predictions',
      component: 'MaintenanceScheduler',
      requiredPermissions: ['maintenance:read'],
      position: 5,
      enabledIndustries: ['manufacturing', 'oil_gas', 'healthcare', 'energy', 'transportation'],
      industryLabels: {
        healthcare: 'Service Windows',
        transportation: 'Fleet Service Schedule',
        iot_telecom: 'Profile Switch Windows',
      },
    },
    {
      id: 'ai-outlook',
      path: 'outlook',
      name: 'AI Outlook',
      icon: 'Brain',
      description: 'AI-generated risk assessment and recommendations',
      component: 'AIOutlook',
      requiredPermissions: ['predictions:read'],
      position: 6,
    },
    {
      id: 'history',
      path: 'history',
      name: 'Historical Patterns',
      icon: 'History',
      description: 'Historical failure patterns and root cause analysis',
      component: 'HistoricalPatterns',
      requiredPermissions: ['predictions:read'],
      position: 7,
      industryLabels: {
        healthcare: 'Device History',
        financial: 'Incident History',
        transportation: 'Fleet History',
        iot_telecom: 'Connectivity History',
      },
    },
  ],

  workflows: [
    {
      id: 'risk-assessment',
      name: 'Risk Assessment',
      description: 'Systematic evaluation of fleet risk posture',
      icon: 'Shield',
      steps: [
        {
          id: 'review-distribution',
          name: 'Review Risk Distribution',
          description: 'Analyze current risk distribution across fleet',
          navigateTo: 'overview',
          icon: 'PieChart',
          estimatedDuration: 5,
        },
        {
          id: 'identify-high-risk',
          name: 'Identify High-Risk Assets',
          description: 'Focus on assets in critical/elevated risk categories',
          navigateTo: 'risk-matrix',
          icon: 'Target',
          estimatedDuration: 5,
        },
        {
          id: 'simulate-scenarios',
          name: 'Run What-If Scenarios',
          description: 'Test different intervention strategies',
          navigateTo: 'scenarios',
          icon: 'FlaskConical',
          estimatedDuration: 15,
        },
        {
          id: 'plan-interventions',
          name: 'Plan Interventions',
          description: 'Schedule maintenance based on risk and ROI',
          navigateTo: 'schedule',
          icon: 'Calendar',
          estimatedDuration: 10,
        },
        {
          id: 'document-decisions',
          name: 'Document Decisions',
          description: 'Record assessment findings and action plan',
          icon: 'FileText',
          estimatedDuration: 5,
        },
      ],
      triggerConditions: ['weeklyReview', 'riskThresholdExceeded'],
    },
    {
      id: 'scenario-planning',
      name: 'Scenario Planning',
      description: 'Evaluate different operational scenarios',
      icon: 'GitBranch',
      steps: [
        {
          id: 'define-scenario',
          name: 'Define Scenario',
          description: 'Set up the what-if scenario parameters',
          icon: 'Settings',
          estimatedDuration: 5,
        },
        {
          id: 'run-simulation',
          name: 'Run Simulation',
          description: 'Execute the scenario simulation',
          navigateTo: 'scenarios',
          icon: 'Play',
          estimatedDuration: 3,
        },
        {
          id: 'analyze-results',
          name: 'Analyze Results',
          description: 'Review simulation outcomes and impacts',
          icon: 'BarChart',
          estimatedDuration: 10,
        },
        {
          id: 'compare-alternatives',
          name: 'Compare Alternatives',
          description: 'Compare multiple scenarios side-by-side',
          icon: 'Columns',
          estimatedDuration: 10,
        },
        {
          id: 'recommend-action',
          name: 'Recommend Action',
          description: 'Select optimal scenario and document rationale',
          icon: 'CheckSquare',
          estimatedDuration: 5,
        },
      ],
    },
  ],

  narratives: {
    sections: [
      {
        id: 'risk-overview',
        name: 'Fleet Risk Overview',
        template: 'Current fleet risk score: {riskScore}/100. {criticalCount} {assetPluralTerm} at critical risk, {elevatedCount} at elevated risk. Overall trend: {trendDirection}.',
        order: 1,
        required: true,
      },
      {
        id: 'risk-distribution',
        name: 'Risk Distribution Analysis',
        template: 'Risk distribution: {criticalPercent}% Critical, {elevatedPercent}% Elevated, {moderatePercent}% Moderate, {lowPercent}% Low. Primary risk factors: {topRiskFactors}.',
        order: 2,
      },
      {
        id: 'trend-analysis',
        name: 'Trend Analysis',
        template: 'Risk trend is {trendDirection} ({changePercent}%) compared to {comparisonPeriod}. {trendExplanation}',
        order: 3,
      },
      {
        id: 'potential-impact',
        name: 'Potential Impact',
        template: '**Potential Impact:**\n• Estimated downtime: {estimatedDowntime}\n• Projected cost: {projectedCost}\n• Affected production: {affectedProduction}',
        order: 4,
        condition: 'hasHighRiskAssets',
      },
      {
        id: 'priority-actions',
        name: 'Priority Actions',
        template: '**Priority Actions:**\n1. {action1}\n2. {action2}\n3. {action3}',
        order: 5,
        required: true,
      },
    ],
    availableVariables: [
      'riskScore', 'criticalCount', 'elevatedCount', 'assetPluralTerm', 'trendDirection',
      'criticalPercent', 'elevatedPercent', 'moderatePercent', 'lowPercent', 'topRiskFactors',
      'changePercent', 'comparisonPeriod', 'trendExplanation',
      'estimatedDowntime', 'projectedCost', 'affectedProduction',
      'action1', 'action2', 'action3',
    ],
  },

  industryOverrides: {
    healthcare: {
      name: 'Risk Assessment',
      description: 'Device risk forecasting and service planning',
      hiddenSubPages: ['schedule'], // Healthcare uses different service scheduling
      additionalSubPages: [
        {
          id: 'compliance-forecast',
          path: 'compliance',
          name: 'Compliance Forecast',
          icon: 'Shield',
          description: 'Predicted compliance status and calibration needs',
          component: 'ComplianceForecast',
          position: 5,
        },
      ],
    },
    financial: {
      name: 'Risk Intelligence',
      description: 'Portfolio risk analysis and stress testing',
      icon: 'TrendingDown',
      additionalSubPages: [
        {
          id: 'var',
          path: 'value-at-risk',
          name: 'Value at Risk',
          icon: 'DollarSign',
          description: 'VaR calculations and risk exposure',
          component: 'ValueAtRisk',
          position: 5,
        },
        {
          id: 'correlations',
          path: 'correlations',
          name: 'Risk Correlations',
          icon: 'Network',
          description: 'System interdependency and correlation analysis',
          component: 'RiskCorrelations',
          position: 6,
        },
      ],
      hiddenSubPages: ['schedule'], // Financial doesn't have physical maintenance
    },
    transportation: {
      name: 'Fleet Predictions',
      description: 'Vehicle risk forecasting and route optimization',
      additionalSubPages: [
        {
          id: 'route-risk',
          path: 'route-risk',
          name: 'Route Risk Analysis',
          icon: 'Map',
          description: 'Risk analysis by route and geography',
          component: 'RouteRiskAnalysis',
          position: 5,
        },
      ],
    },
    iot_telecom: {
      name: 'Connectivity Predictions',
      description: 'SIM fleet connectivity forecasting with Multi-IMSI optimization',
      icon: 'Radio',
      additionalSubPages: [
        {
          id: 'connectivity-forecast',
          path: 'connectivity',
          name: 'Connectivity Forecast',
          icon: 'Signal',
          description: 'Predict SIM connectivity issues before they occur',
          component: 'ConnectivityForecast',
          position: 5,
        },
        {
          id: 'carrier-performance',
          path: 'carriers',
          name: 'Carrier Performance Prediction',
          icon: 'TrendingUp',
          description: 'Forecast carrier network performance and outages',
          component: 'CarrierPerformancePrediction',
          position: 6,
        },
        {
          id: 'data-quota',
          path: 'quota',
          name: 'Data Quota Forecast',
          icon: 'Database',
          description: 'Predict data usage and quota exhaustion',
          component: 'DataQuotaForecast',
          position: 7,
        },
        {
          id: 'coverage-prediction',
          path: 'coverage-pred',
          name: 'Coverage Prediction',
          icon: 'Map',
          description: 'Predict coverage gaps for mobile SIM deployments',
          component: 'CoveragePrediction',
          position: 8,
        },
        {
          id: 'cost-projection',
          path: 'cost-projection',
          name: 'Cost Projection',
          icon: 'DollarSign',
          description: 'Forecast connectivity costs and identify optimization opportunities',
          component: 'CostProjection',
          position: 9,
        },
        {
          id: 'network-optimizer',
          path: 'optimizer',
          name: 'Network Optimizer',
          icon: 'Cpu',
          description: 'AI-powered Multi-IMSI profile optimization recommendations',
          component: 'NetworkOptimizer',
          position: 10,
        },
      ],
      additionalWorkflows: [
        {
          id: 'connectivity-risk-assessment',
          name: 'Connectivity Risk Assessment',
          description: 'Comprehensive SIM fleet connectivity risk evaluation',
          icon: 'Shield',
          steps: [
            {
              id: 'review-connectivity',
              name: 'Review Connectivity Status',
              description: 'Assess current SIM fleet connectivity health',
              navigateTo: 'overview',
              icon: 'Gauge',
              estimatedDuration: 3,
            },
            {
              id: 'forecast-issues',
              name: 'Forecast Connectivity Issues',
              description: 'Identify SIMs at risk of connectivity loss',
              navigateTo: 'connectivity-forecast',
              icon: 'AlertTriangle',
              estimatedDuration: 5,
            },
            {
              id: 'check-carriers',
              name: 'Check Carrier Performance',
              description: 'Review carrier network predictions',
              navigateTo: 'carrier-performance',
              icon: 'TrendingUp',
              estimatedDuration: 5,
            },
            {
              id: 'optimize-profiles',
              name: 'Optimize Network Profiles',
              description: 'Get AI recommendations for Multi-IMSI optimization',
              navigateTo: 'network-optimizer',
              icon: 'Cpu',
              estimatedDuration: 5,
            },
          ],
          triggerConditions: ['weeklyReview', 'connectivityRiskElevated'],
        },
        {
          id: 'cost-optimization',
          name: 'Cost Optimization Planning',
          description: 'Plan connectivity cost reductions',
          icon: 'DollarSign',
          steps: [
            {
              id: 'project-costs',
              name: 'Project Current Costs',
              description: 'Review cost trajectory for SIM fleet',
              navigateTo: 'cost-projection',
              icon: 'DollarSign',
              estimatedDuration: 5,
            },
            {
              id: 'analyze-quota',
              name: 'Analyze Data Usage',
              description: 'Review data quota forecasts',
              navigateTo: 'data-quota',
              icon: 'Database',
              estimatedDuration: 5,
            },
            {
              id: 'get-recommendations',
              name: 'Get Optimization Recommendations',
              description: 'AI-powered cost reduction suggestions',
              navigateTo: 'network-optimizer',
              icon: 'Cpu',
              estimatedDuration: 5,
            },
          ],
          triggerConditions: ['monthlyReview', 'costThresholdExceeded'],
        },
      ],
    },
    datacenter: {
      name: 'Infrastructure Predictions',
      description: 'Server failure forecasting, cooling capacity planning, and power load optimization',
      icon: 'Server',
    },
  },

  requiredPermissions: ['predictions:read'],
}
